export interface CalendarDate {
  day: number
  month: number
  year: number
}

export interface TimezoneDisplayOption {
  label: string
  location: string
  offsetLabel: string
  offsetMinutes: number
  searchText: string
  summaryLabel: string
  timezone: string
}

const fallbackTimezones = [
  'UTC',
  'Africa/Johannesburg',
  'America/Chicago',
  'America/Los_Angeles',
  'America/New_York',
  'America/Sao_Paulo',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
]

const localizedLocations: Record<'en' | 'zh', Record<string, string>> = {
  en: {
    UTC: 'UTC',
    'Africa/Johannesburg': 'Johannesburg',
    'America/Chicago': 'Chicago',
    'America/Los_Angeles': 'Los Angeles',
    'America/New_York': 'New York',
    'America/Sao_Paulo': 'São Paulo',
    'Asia/Dubai': 'Dubai',
    'Asia/Hong_Kong': 'Hong Kong',
    'Asia/Seoul': 'Seoul',
    'Asia/Shanghai': 'Shanghai',
    'Asia/Singapore': 'Singapore',
    'Asia/Tokyo': 'Tokyo',
    'Australia/Sydney': 'Sydney',
    'Europe/Berlin': 'Berlin',
    'Europe/London': 'London',
    'Europe/Paris': 'Paris',
  },
  zh: {
    UTC: '协调世界时',
    'Africa/Johannesburg': '约翰内斯堡',
    'America/Chicago': '芝加哥',
    'America/Los_Angeles': '洛杉矶',
    'America/New_York': '纽约',
    'America/Sao_Paulo': '圣保罗',
    'Asia/Dubai': '迪拜',
    'Asia/Hong_Kong': '香港',
    'Asia/Seoul': '首尔',
    'Asia/Shanghai': '上海',
    'Asia/Singapore': '新加坡',
    'Asia/Tokyo': '东京',
    'Australia/Sydney': '悉尼',
    'Europe/Berlin': '柏林',
    'Europe/London': '伦敦',
    'Europe/Paris': '巴黎',
  },
}

function getLocaleFamily(locale: string): 'en' | 'zh' {
  return locale.toLowerCase().startsWith('zh') ? 'zh' : 'en'
}

function getTimezoneLocation(timezone: string, locale: string) {
  const localeFamily = getLocaleFamily(locale)
  const localizedLocation = localizedLocations[localeFamily][timezone]
  if (localizedLocation) return localizedLocation

  return (timezone.split('/').at(-1) ?? timezone).replaceAll('_', ' ')
}

function getTimezoneName(
  timezone: string,
  locale: string,
  date: Date,
  timeZoneName: 'longGeneric' | 'longOffset',
) {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    timeZoneName,
  })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value
}

function getOffsetMinutes(timezone: string, date: Date) {
  const offsetName = getTimezoneName(timezone, 'en-US', date, 'longOffset') ?? 'GMT'
  const match = /^GMT(?<sign>[+-])(?<hours>\d{2}):(?<minutes>\d{2})$/.exec(offsetName)
  if (!match?.groups) return 0

  const absoluteMinutes = Number(match.groups.hours) * 60 + Number(match.groups.minutes)
  return match.groups.sign === '-' ? -absoluteMinutes : absoluteMinutes
}

function formatOffset(offsetMinutes: number) {
  const sign = offsetMinutes < 0 ? '-' : '+'
  const absoluteMinutes = Math.abs(offsetMinutes)
  const hours = Math.floor(absoluteMinutes / 60)
    .toString()
    .padStart(2, '0')
  const minutes = (absoluteMinutes % 60).toString().padStart(2, '0')
  return `UTC${sign}${hours}:${minutes}`
}

function getOffsetSearchTerms(offsetMinutes: number) {
  const sign = offsetMinutes < 0 ? '-' : '+'
  const absoluteMinutes = Math.abs(offsetMinutes)
  const hours = Math.floor(absoluteMinutes / 60)
  const paddedHours = hours.toString().padStart(2, '0')
  const minutes = (absoluteMinutes % 60).toString().padStart(2, '0')
  const shortOffset = `${sign}${String(hours)}`
  const fullOffset = `${sign}${paddedHours}:${minutes}`

  return [
    `UTC${shortOffset}`,
    `GMT${shortOffset}`,
    `UTC${fullOffset}`,
    `GMT${fullOffset}`,
    shortOffset,
    fullOffset,
  ].join(' ')
}

function normalizeTimezoneSearch(value: string) {
  return value.normalize('NFKC').replaceAll('_', ' ').trim().toLocaleLowerCase()
}

/**
 * 读取当前设备的 IANA timezone；浏览器能力不可用时让后端按 UTC fallback。
 */
export function getDeviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

/**
 * 返回浏览器支持的 timezone 建议；旧浏览器没有 supportedValuesOf 时提供常用 fallback。
 * additionalValues 用于保留 UTC、设备 timezone 和服务端已有 alias，不限制用户手工输入。
 */
export function getSupportedTimezones(additionalValues: (string | null | undefined)[] = []) {
  let supportedValues: string[] = []

  try {
    const supportedValuesOf: unknown = Reflect.get(Intl, 'supportedValuesOf')
    if (typeof supportedValuesOf === 'function') {
      supportedValues = Reflect.apply(supportedValuesOf, Intl, ['timeZone']) as string[]
    }
  } catch {
    supportedValues = []
  }

  return Array.from(
    new Set(
      [...fallbackTimezones, ...supportedValues, ...additionalValues]
        .map((timezone) => timezone?.trim())
        .filter((timezone): timezone is string => Boolean(timezone)),
    ),
  ).sort((left, right) => left.localeCompare(right))
}

/**
 * 为 timezone 选择器生成本地化名称、当前 UTC offset 与搜索文本。
 * IANA timezone 仍是唯一保存值；offset 只描述当前日期，不能代替 DST 规则。
 */
export function getTimezoneDisplayOptions(
  timezones: string[],
  locale: string,
  date = new Date(),
): TimezoneDisplayOption[] {
  const localeFamily = getLocaleFamily(locale)
  const collator = new Intl.Collator(locale)

  return timezones
    .map((timezone) => {
      try {
        const offsetMinutes = getOffsetMinutes(timezone, date)
        const offsetLabel = formatOffset(offsetMinutes)
        const location = getTimezoneLocation(timezone, locale)
        const genericName =
          getTimezoneName(timezone, locale, date, 'longGeneric') ?? timezone.replaceAll('_', ' ')
        const normalizedGenericName =
          timezone === 'UTC' || /^GMT(?:[+-]00(?::00)?)?$/.test(genericName)
            ? localeFamily === 'zh'
              ? '协调世界时'
              : 'Coordinated Universal Time'
            : genericName
        const locationSuffix =
          timezone === 'UTC' ? '' : localeFamily === 'zh' ? `（${location}）` : ` (${location})`
        const label = `(${offsetLabel}) ${normalizedGenericName}${locationSuffix}`
        const summaryLabel = `${offsetLabel} ${normalizedGenericName}`

        return {
          label,
          location,
          offsetLabel,
          offsetMinutes,
          searchText: [
            label,
            summaryLabel,
            location,
            timezone,
            timezone.replaceAll('/', ' ').replaceAll('_', ' '),
            getOffsetSearchTerms(offsetMinutes),
          ].join(' '),
          summaryLabel,
          timezone,
        }
      } catch {
        return {
          label: timezone,
          location: getTimezoneLocation(timezone, locale),
          offsetLabel: '',
          offsetMinutes: Number.POSITIVE_INFINITY,
          searchText: normalizeTimezoneSearch(timezone),
          summaryLabel: timezone,
          timezone,
        }
      }
    })
    .sort(
      (left, right) =>
        left.offsetMinutes - right.offsetMinutes ||
        collator.compare(left.label, right.label) ||
        left.timezone.localeCompare(right.timezone),
    )
}

/**
 * 按指定 timezone 提取 Gregorian 日历日期，供聊天分组等纯展示逻辑复用。
 */
export function getCalendarDate(date: Date, timeZone?: string): CalendarDate {
  const parts = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
    day: '2-digit',
    month: '2-digit',
    timeZone,
    year: 'numeric',
  }).formatToParts(date)

  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))

  return {
    day: Number(values.day),
    month: Number(values.month),
    year: Number(values.year),
  }
}

/**
 * 使用当前界面 locale、但在明确的 timezone 下格式化时间。
 */
export function formatDateInTimezone(
  date: Date,
  options: Intl.DateTimeFormatOptions,
  timeZone?: string,
) {
  return new Intl.DateTimeFormat(undefined, {
    ...options,
    timeZone,
  }).format(date)
}
